import { useCallback, useEffect, useMemo, useState } from "react";
import imageCompression from "browser-image-compression";
import { FileUploadType } from "@fixa/platform-shared/dist/fileUpload/fileUpload.type";
import { useWriteSignedUrls } from "@coworker/apprestructured/src/task-management/services/useWriteSignedUrls";
import { useUploadFilesToBucket } from "@coworker/apprestructured/src/task-management/services/useUploadFilesToBucket";
import {
  calcHasSignedUrlsForAllFiles,
  convertToSignedFiles,
  filesToCreateUploads,
  NO_CREATE_UPLOADS,
  UploaderResult,
} from "@coworker/apprestructured/src/uploader/uploader.helper";

type ImageUploaderOptions = {
  maxWidthOrHeight?: number;
};

/**
 * Helper for uploading images to a bucket.
 * Images are compressed, resized and converted to image/jpeg before uploading.
 *
 * @param images images to upload
 * @param options compression options
 */
export function useImageUploader(
  images: File[],
  options: ImageUploaderOptions = DEFAULT_OPTIONS
): UploaderResult {
  const [compressedImages, setCompressedImages] = useState<File[]>([]);

  // Needs to be an effect because it is async
  useEffect(() => {
    async function fn() {
      setCompressedImages(await compressImages(images, options));
    }

    fn();
  }, [images, options]);

  const allImagesAreCompressed = useMemo(() => {
    return calcAllImagesAreCompressed(images, compressedImages);
  }, [compressedImages, images]);

  const compressedImageFileNames = useMemo(() => {
    return compressedImages.map((file) => file.name);
  }, [compressedImages]);

  const { data: signedUrlResponses } = useWriteSignedUrls(
    compressedImageFileNames,
    FileUploadType.IMAGE
  );

  const hasSignedUrlsForAllCompressedImages = useMemo(() => {
    return calcHasSignedUrlsForAllFiles(compressedImages, signedUrlResponses);
  }, [compressedImages, signedUrlResponses]);

  const { mutateAsync: uploadFilesToBucketMutation } = useUploadFilesToBucket();

  const ready = allImagesAreCompressed && hasSignedUrlsForAllCompressedImages;

  const upload = useCallback(() => {
    if (!ready) {
      throw new Error("Image uploader is not ready");
    }
    const signedFiles = convertToSignedFiles(
      compressedImages,
      signedUrlResponses
    );
    return uploadFilesToBucketMutation(signedFiles);
  }, [
    compressedImages,
    ready,
    uploadFilesToBucketMutation,
    signedUrlResponses,
  ]);

  const createUploads = useMemo(() => {
    if (!hasSignedUrlsForAllCompressedImages) {
      return NO_CREATE_UPLOADS;
    }
    return filesToCreateUploads(compressedImages, signedUrlResponses);
  }, [
    compressedImages,
    hasSignedUrlsForAllCompressedImages,
    signedUrlResponses,
  ]);

  return { ready, upload, createUploads };
}

const DEFAULT_MAX_WIDTH_OR_HEIGHT = 1000;

const DEFAULT_OPTIONS: ImageUploaderOptions = {
  maxWidthOrHeight: DEFAULT_MAX_WIDTH_OR_HEIGHT,
};

async function compressImages(images: File[], options: ImageUploaderOptions) {
  return Promise.all(images.map((image) => compressImage(image, options)));
}

async function compressImage(image: File, options: ImageUploaderOptions) {
  const compressedImage = await imageCompression(image, {
    ...options,
    fileType: "image/jpeg",
  });

  return new File(
    [compressedImage],
    image.name.replace(/\.[^/.]+$/, "") + ".jpg",
    { type: compressedImage.type, lastModified: Date.now() }
  );
}

function calcAllImagesAreCompressed(images: File[], compressedImages: File[]) {
  if (images.length !== compressedImages.length) {
    return false;
  }

  const imageNamesWithoutExtension = calcFileNamesWithoutExtension(images);
  const compressedImageNamesWithoutExtension =
    calcFileNamesWithoutExtension(compressedImages);
  for (let i = 0; i < images.length; i++) {
    if (
      imageNamesWithoutExtension[i] !== compressedImageNamesWithoutExtension[i]
    ) {
      return false;
    }
  }
  return true;
}

function calcFileNamesWithoutExtension(files: File[]) {
  return files.map(calcFileNameWithoutExtension);
}

function calcFileNameWithoutExtension(file: File) {
  if (!file.name.includes(".")) {
    return file.name;
  }
  return file.name.substring(0, file.name.lastIndexOf("."));
}
